// services/aggregateHelper.ts

type AggregateType = 'average' | 'total' | 'min' | 'max';
type UpdateCallback = (svgLabelName: string, value: string) => void;

interface AggregateTarget {
  type: AggregateType;
    dictionaryKeys: string[];
}

const dataStore: Record<string, number[]> = {};
const aggregateTargets: Record<string, AggregateTarget> = {};
let updateCallback: UpdateCallback = () => {};

/**
 * Initializes the helper with a callback function to update the SVG.
 */
export const initAggregateHelper = (callback: UpdateCallback): void => {
    updateCallback = callback;
};

/**
 * Registers an SVG label to display an aggregate calculation.
 */
export const registerAggregate = (svgLabelName: string, type: AggregateType, dictionaryKeys: string[]): void => {
    aggregateTargets[svgLabelName] = {
      type,
      dictionaryKeys: (dictionaryKeys || []).map((k) => String(k).toLowerCase()),
    };
};

/**
 * Ingests a new register value and recalculates affected aggregates.
 */
export const handleValueUpdate = (dictionaryKey: string, newValue: number): void => {
    const normalizedKey = String(dictionaryKey || '').toLowerCase();
    if (!normalizedKey) return;
    if (!dataStore[normalizedKey]) dataStore[normalizedKey] = [];
    dataStore[normalizedKey].push(newValue);
    
    // Check if this update affects any registered aggregate targets
    for (const svgLabelName in aggregateTargets) {
        const target = aggregateTargets[svgLabelName];
        const isMatch = target.dictionaryKeys.includes(normalizedKey);
        
        if (isMatch) {
            recalculateAggregate(svgLabelName, target);
        }
    }
};

/**
 * Performs the actual calculation and calls the main application callback.
 */
function recalculateAggregate(svgLabelName: string, target: AggregateTarget): void {
    // Flatten values for all target dictionary keys
    const relevantValues = target.dictionaryKeys
        .flatMap((key) => dataStore[key] || [])
        .filter((v) => typeof v === 'number' && !isNaN(v));

    if (relevantValues.length === 0) {
        updateCallback(svgLabelName, "N/A");
        return;
    }

    let result: number;
    switch (target.type) {
        case 'average': {
            const sum = relevantValues.reduce((a, b) => a + b, 0);
            result = sum / relevantValues.length;
            break;
        }
        case 'total':
            result = relevantValues.reduce((a, b) => a + b, 0);
            break;
        case 'min':
            result = Math.min(...relevantValues);
            break;
        case 'max':
            result = Math.max(...relevantValues);
            break;
        default:
            result = NaN;
    }

    // Format the result nicely
    const formattedResult = (target.type === 'average' ? result.toFixed(2) : result.toFixed(1)).replace(/\.?0+$/, "");
    
    // Send the formatted result back to initSchematics to update the SVG
    updateCallback(svgLabelName, formattedResult);
}
